import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api, apiErrorMessage } from '../../services/api'
import { Badge, Button, Card, EmptyState, ErrorText, Spinner } from '../../components/ui'
import { Icon, type IconName } from '../../components/ui/icons'
import { PrintableOrder } from '../../components/print/PrintableOrder'
import {
  APTITUDE_COLORS,
  APTITUDE_LABELS,
  CLINICAL_ORDER_TYPE_COLORS,
  CLINICAL_ORDER_TYPE_LABELS,
  formatDate,
} from '../../utils/format'
import type { ClinicalOrder, ClinicalOrderType } from '../../types/api'

/** Fila del listado: metadatos del documento, sin su contenido. */
interface PortalClinicalOrder {
  id: string
  type: ClinicalOrderType
  verificationCode: string
  issuedAt: string
  sessions?: number | null
  itemCount: number
  professional: { firstName: string; lastName: string; specialty?: string | null }
}

interface PortalCertificate {
  id: string
  certificateCode: string
  result: string
  resultNotes?: string | null
  jobTitle?: string | null
  completedAt?: string | null
  battery: { name: string; jobProfile?: string | null }
  company?: { businessName: string } | null
}

const TYPE_ICON: Record<ClinicalOrderType, IconName> = {
  PRESCRIPTION: 'prescription',
  EXAM_REQUEST: 'lab',
  KINESIOLOGY: 'rehab',
}

/** Resumen de una línea: qué trae el documento, sin abrirlo. */
function summary(order: PortalClinicalOrder): string {
  if (order.type === 'KINESIOLOGY') return `${order.sessions ?? 0} sesiones indicadas`
  const noun = order.type === 'PRESCRIPTION' ? 'medicamento' : 'examen'
  return `${order.itemCount} ${noun}${order.itemCount === 1 ? '' : 's'}`
}

/**
 * Recetas y certificados (pedido de Chejo, minuta 15-09). Son los documentos
 * que la clínica ya emite en la atención y hasta ahora solo existían en papel:
 * recetas, solicitudes de exámenes, órdenes de kinesiología y certificados
 * preocupacionales.
 *
 * El listado trae solo metadatos; el contenido se pide al abrirlo y esa
 * apertura queda auditada, igual que un examen. El certificado se abre en la
 * página pública de verificación: es el mismo documento que valida el
 * empleador, con su código, y así existe una sola versión del papel.
 */
export function MisRecetasPage() {
  const [viewing, setViewing] = useState<ClinicalOrder | null>(null)
  const [loadingId, setLoadingId] = useState<string | null>(null)
  const [error, setError] = useState('')

  const orders = useQuery({
    queryKey: ['patient-clinical-orders'],
    queryFn: async () => (await api.get<PortalClinicalOrder[]>('/patient-portal/clinical-orders')).data,
  })
  const certificates = useQuery({
    queryKey: ['patient-certificates'],
    queryFn: async () => (await api.get<PortalCertificate[]>('/patient-portal/certificates')).data,
  })

  async function open(id: string) {
    setError('')
    setLoadingId(id)
    try {
      const { data } = await api.get<ClinicalOrder>(`/patient-portal/clinical-orders/${id}`)
      setViewing(data)
    } catch (err) {
      setError(apiErrorMessage(err))
    } finally {
      setLoadingId(null)
    }
  }

  const list = orders.data ?? []
  const certs = certificates.data ?? []

  return (
    <>
      <Card title="Recetas y órdenes médicas">
        <ErrorText>{error}</ErrorText>
        {orders.isLoading ? (
          <Spinner />
        ) : list.length === 0 ? (
          <EmptyState message="Todavía no tienes recetas ni órdenes emitidas. Aparecen aquí apenas tu médico las emite en la consulta." />
        ) : (
          <ul className="space-y-2">
            {list.map((order) => (
              <li
                key={order.id}
                className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-gray-200 p-3"
              >
                <div className="min-w-0">
                  <p className="flex flex-wrap items-center gap-2 font-medium text-ink">
                    <Icon name={TYPE_ICON[order.type]} className="h-4 w-4 text-steel-500" />
                    {CLINICAL_ORDER_TYPE_LABELS[order.type]}
                    <Badge className={CLINICAL_ORDER_TYPE_COLORS[order.type]}>{summary(order)}</Badge>
                  </p>
                  <p className="text-xs text-gray-500">
                    {formatDate(order.issuedAt)} · {order.professional.firstName} {order.professional.lastName}
                    {order.professional.specialty ? ` · ${order.professional.specialty}` : ''}
                  </p>
                  <p className="font-mono text-xs text-gray-400">Código {order.verificationCode}</p>
                </div>
                <Button variant="secondary" className="shrink-0" onClick={() => open(order.id)} disabled={loadingId === order.id}>
                  <Icon name="view" className="mr-1.5 inline h-4 w-4" />
                  {loadingId === order.id ? 'Abriendo…' : 'Ver documento'}
                </Button>
              </li>
            ))}
          </ul>
        )}
        <p className="mt-3 text-xs text-gray-400">
          Desde el documento puedes imprimirlo o guardarlo como PDF. Cada vez que lo abres queda registrado; el código
          del pie permite a la farmacia o al laboratorio comprobar que es auténtico.
        </p>
      </Card>

      <Card title="Certificados preocupacionales">
        {certificates.isLoading ? (
          <Spinner />
        ) : certs.length === 0 ? (
          <EmptyState message="No tienes certificados preocupacionales emitidos." />
        ) : (
          <ul className="space-y-2">
            {certs.map((cert) => (
              <li
                key={cert.id}
                className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-gray-200 p-3"
              >
                <div className="min-w-0">
                  <p className="flex flex-wrap items-center gap-2 font-medium text-ink">
                    <Icon name="certificate" className="h-4 w-4 text-steel-500" />
                    {cert.battery.name}
                    <Badge className={APTITUDE_COLORS[cert.result]}>{APTITUDE_LABELS[cert.result]}</Badge>
                  </p>
                  <p className="text-xs text-gray-500">
                    {cert.completedAt ? formatDate(cert.completedAt) : 'Sin fecha'}
                    {cert.jobTitle ? ` · Cargo: ${cert.jobTitle}` : ''}
                    {cert.company ? ` · ${cert.company.businessName}` : ''}
                  </p>
                  <p className="font-mono text-xs text-gray-400">Código {cert.certificateCode}</p>
                  {cert.resultNotes && <p className="mt-1 text-sm text-steel-700">{cert.resultNotes}</p>}
                </div>
                <a
                  href={`/verificar?codigo=${encodeURIComponent(cert.certificateCode)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex shrink-0 items-center gap-1.5 rounded-lg border border-steel-300 bg-white px-3.5 py-2 text-sm font-medium text-ink hover:bg-soft"
                >
                  <Icon name="view" className="h-4 w-4" />
                  Ver certificado
                </a>
              </li>
            ))}
          </ul>
        )}
      </Card>

      {viewing && <PrintableOrder order={viewing} onClose={() => setViewing(null)} />}
    </>
  )
}
